/**
 * PetiteEnfancePlanningTravailPage — Planning de travail des agents petite enfance.
 *
 * Même schéma que le GRC/scolaire : grille hebdo, affectation des agents aux
 * STRUCTURES (EAJE) par glisser-déposer, profils récurrents + génération sur
 * période, transmission à la paie. Le taux d'encadrement PMI reste géré sur la
 * page Pointage (inchangé).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Send, Plus, GripVertical } from 'lucide-react';
import {
  DndContext, PointerSensor, useSensor, useSensors, useDraggable,
  type DragEndEvent, DragOverlay,
} from '@dnd-kit/core';
import { api, ApiException, type PePlanningCreneau, type PeStructure } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { Button, Alert, Field } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { BoutonCloturePeriode } from '@/components/planning/BoutonCloturePeriode';
import { PlanningGrilleUnifiee, type CreneauGrille } from '@/components/planning/PlanningGrilleUnifiee';
import { PlanningVueEquipe } from '@/components/planning/PlanningVueEquipe';
import { PostesManagerUnifie, type PostesPlanningApi } from '@/components/planning/PostesManagerUnifie';
import { interpretPlanningDrop } from '@/components/planning/planning-drag';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { useToast } from '@/components/ui/Toast';
import { PetiteEnfanceLayout } from '@/components/petite-enfance/PetiteEnfanceLayout';
import { DeclarerAbsenceModal } from '@/pages/service-technique/DeclarerAbsenceModal';
import type { ProfilPlage } from '@roronoa-collectivites/shared-types';

const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
function ymd(d: Date): string { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
function addDays(d: Date, n: number): Date { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfWeek(d = new Date()): Date { const x = new Date(d); const dow = x.getDay() === 0 ? 7 : x.getDay(); x.setDate(x.getDate() - (dow - 1)); x.setHours(0, 0, 0, 0); return x; }
function hhmm(iso: string): string { const d = new Date(iso); return `${String(d.getHours()).padStart(2, '0')}h${d.getMinutes() ? String(d.getMinutes()).padStart(2, '0') : ''}`; }

export function PetiteEnfancePlanningTravailPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { confirm, dialogElement } = useConfirmDialog();
  const isAgent = user?.role === 'agent';
  // Refonte planning 21/07 : vue Équipe (défaut) / Individuelle
  const [vue, setVue] = useState<'equipe' | 'individuelle'>('equipe');
  const [agentIndividuel, setAgentIndividuel] = useState<string>('');

  const [weekStart, setWeekStart] = useState<Date>(startOfWeek());
  const [creneaux, setCreneaux] = useState<PePlanningCreneau[]>([]);
  const [structures, setStructures] = useState<PeStructure[]>([]);
  const [agents, setAgents] = useState<{ id: string; fullName: string; role: string; couleurMarker: string | null }[]>([]);
  const [nonTravailles, setNonTravailles] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profilsOpen, setProfilsOpen] = useState(false);
  const [postesOpen, setPostesOpen] = useState(false);
  const [dragStruct, setDragStruct] = useState<PeStructure | null>(null);
  const [affect, setAffect] = useState<{ structure: PeStructure; jour: Date } | null>(null);
  // Déclaration d'absence catégorisée + édition de créneau (comme les autres briques)
  const [absDecl, setAbsDecl] = useState<{ agent: { id: string; fullName: string }; jourISO: string } | null>(null);
  const [editCreneau, setEditCreneau] = useState<PePlanningCreneau | null>(null);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const from = ymd(weekStart);
      const to = ymd(addDays(weekStart, 7));
      const [pl, str, jnt] = await Promise.all([
        api.petiteEnfance.planning.list(from, to),
        api.petiteEnfance.structures.list(),
        api.petiteEnfance.planning.joursNonTravailles(from, to).catch(() => []),
      ]);
      setCreneaux(pl.creneaux);
      setStructures(str.structures.filter((s) => s.actif));
      setNonTravailles(new Map(jnt.map((j) => [j.date, j.libelle])));
      if (!isAgent) {
        const ag = await api.petiteEnfance.planning.listAgents().catch(() => ({ items: [] }));
        setAgents(ag.items);
      }
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de chargement'); }
    finally { setLoading(false); }
  }, [weekStart, isAgent]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!agentIndividuel && agents.length > 0) setAgentIndividuel(agents[0]!.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents]);


  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  function onDragEnd(event: DragEndEvent) {
    setDragStruct(null);
    // Déplacement d'un créneau posé (durée conservée) — même mécanique que partout.
    const res = interpretPlanningDrop<never>(event, weekDays);
    if (res?.kind === 'move') {
      void (async () => {
        try {
          await api.petiteEnfance.planning.update(res.creneau.id, {
            dateDebut: res.nouveauDebut.toISOString(),
            dateFin: res.nouvelleFin.toISOString(),
          });
          void load();
        } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur lors du déplacement'); }
      })();
      return;
    }
    // Dépose d'un établissement (spécificité PE).
    const { active, over } = event;
    if (!over) return;
    const overData = over.data.current as { dayIdx?: number; hour?: number } | undefined;
    const activeData = active.data.current as { structure?: PeStructure } | undefined;
    if (!overData || !activeData?.structure || overData.dayIdx == null || overData.hour == null) return;
    const jour = new Date(weekDays[overData.dayIdx]!);
    jour.setHours(overData.hour, 0, 0, 0);
    // En vue individuelle, l'agent est déjà choisi → affectation directe (2 h par défaut).
    if (vue === 'individuelle' && agentIndividuel) {
      const fin = new Date(jour.getTime() + 2 * 3600 * 1000);
      void (async () => {
        try {
          await api.petiteEnfance.planning.create({ agentUserId: agentIndividuel, structureId: activeData.structure!.id, dateDebut: jour.toISOString(), dateFin: fin.toISOString() });
          void load();
        } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur lors de l\'affectation'); }
      })();
      return;
    }
    setAffect({ structure: activeData.structure, jour });
  }

  // Mapping des créneaux PE vers la forme générique de la grille partagée.
  const creneauxGrille: CreneauGrille[] = creneaux.map((c) => ({
    id: c.id,
    agentUserId: c.agentUserId,
    agentFullName: c.agentNom ?? '—',
    agentCouleurMarker: null,
    dateDebut: c.dateDebut,
    dateFin: c.dateFin,
    titre: c.structureNom ?? c.agentNom ?? '—',
    description: null,
    signalementId: null,
    signalementNumero: null,
    type: c.type ?? 'accueil',
    couleurMarker: null,
  }));
  const days = weekDays.map((d) => ({ label: JOURS[d.getDay() === 0 ? 6 : d.getDay() - 1]!, date: d }));
  const todayKey = new Date().toDateString();
  const colorAgentById = new Map(agents.map((a) => [a.id, a.couleurMarker ?? null]));
  const colorOfCreneauPe = (c: CreneauGrille): string => {
    if (creneaux.find((x) => x.id === c.id)?.statut === 'absent') return '#b91c1c';
    return colorAgentById.get(c.agentUserId) ?? '#8b5cf6';
  };

  const agentsVueEquipe = agents.map((a) => ({ id: a.id, fullName: a.fullName, couleurMarker: a.couleurMarker ?? null }));
  async function setCouleurAgent(agentId: string, couleur: string | null) {
    try { await api.petiteEnfance.planning.setAgentCouleur(agentId, couleur); void load(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur couleur'); }
  }
  const postesApiPe: PostesPlanningApi = {
    listPostes: () => api.petiteEnfance.planning.listPostes().then((ps) => ps.map((p) => ({ id: p.id, titre: p.titre, dureeMin: p.dureeMin, couleur: p.couleur ?? null }))),
    createPoste: (b) => api.petiteEnfance.planning.createPoste(b).then((p) => ({ id: p.id, titre: p.titre, dureeMin: p.dureeMin, couleur: p.couleur ?? null })),
    deletePoste: (id) => api.petiteEnfance.planning.deletePoste(id),
    // PE : l'affectation d'un poste depuis la modale crée un créneau via la première structure.
    createCreneau: async (b) => {
      const struct = structures[0];
      if (!struct) throw new Error('Aucun établissement configuré.');
      return api.petiteEnfance.planning.create({ agentUserId: b.agentUserId, structureId: struct.id, dateDebut: b.dateDebut, dateFin: b.dateFin });
    },
  };

  async function transmettrePaie() {
    try {
      const r = await api.petiteEnfance.planning.transmettrePaie(ymd(weekStart), ymd(addDays(weekStart, 6)));
      toast.success(`${r.transmis} créneau(x) transmis à la paie.`);
      void load();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  async function supprimer(c: PePlanningCreneau) {
    try { await api.petiteEnfance.planning.remove(c.id); void load(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  async function basculerPresent(c: PePlanningCreneau) {
    try { await api.petiteEnfance.planning.setStatut(c.id, 'planifie'); void load(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  return (
    <PetiteEnfanceLayout activeTab="planning-travail">
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {/* Sélecteur Équipe / Individuelle (service bloqué = petite enfance) */}
          <div className="inline-flex rounded-r-lg border border-border-hi bg-surface/60 p-0.5">
            <button type="button" onClick={() => setVue('equipe')} className={`rounded-r-md px-3 py-1.5 text-sm ${vue === 'equipe' ? 'bg-petite_enfance text-white' : 'text-ink-muted'}`}>Équipe</button>
            <button type="button" onClick={() => setVue('individuelle')} className={`rounded-r-md px-3 py-1.5 text-sm ${vue === 'individuelle' ? 'bg-petite_enfance text-white' : 'text-ink-muted'}`}>Individuelle</button>
          </div>
          {!isAgent && (
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <Button variant="secondary" size="sm" onClick={() => setProfilsOpen(true)}>Profils &amp; génération</Button>
              <Button variant="secondary" size="sm" onClick={() => setPostesOpen(true)}>Postes</Button>
              <Button variant="secondary" size="sm" onClick={() => void transmettrePaie()}>
                <Send className="h-4 w-4" aria-hidden="true" /><span className="hidden sm:inline">Transmettre à la paie</span>
              </Button>
              <BoutonCloturePeriode service="petite_enfance" />
            </div>
          )}
        </div>
        {error && <Alert variant="error" className="mb-4">{error}</Alert>}

        <div className="mb-4 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setWeekStart(startOfWeek(addDays(weekStart, -7)))}>← Semaine préc.</Button>
          <span className="text-sm font-medium text-ink">Semaine du {weekStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
          <Button variant="ghost" size="sm" onClick={() => setWeekStart(startOfWeek(addDays(weekStart, 7)))}>Semaine suiv. →</Button>
        </div>

        {loading ? (
          <p className="text-sm text-ink-muted">Chargement…</p>
        ) : vue === 'equipe' ? (
          /* VUE ÉQUIPE (défaut) — 1 ligne/agent, cadres colorés alignés */
          <PlanningVueEquipe
            days={days}
            agents={agentsVueEquipe}
            creneaux={creneauxGrille}
            colorOfCreneau={colorOfCreneauPe}
            onSetAgentColor={isAgent ? undefined : setCouleurAgent}
            onCreneauClick={(c) => { const orig = creneaux.find((x) => x.id === c.id); if (orig && !isAgent) setEditCreneau(orig); }}
            /* LOT 2 — déclencheur du modal d'absence depuis la grille (manager). */
            onDeclarerAbsence={isAgent ? undefined : (agent, jourISO) =>
              setAbsDecl({ agent: { id: agent.id, fullName: agent.fullName }, jourISO })}
          />
        ) : (
          /* VUE INDIVIDUELLE — grille horaire d'UN agent (glisser un établissement pour créer) */
          <DndContext sensors={sensors} onDragStart={(e) => { const d = e.active.data.current as { structure?: PeStructure } | undefined; setDragStruct(d?.structure ?? null); }} onDragEnd={onDragEnd}>
            {!isAgent && (
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <label className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Agent :</label>
                <select value={agentIndividuel} onChange={(e) => setAgentIndividuel(e.target.value)} className="h-9 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
                  {agents.map((a) => <option key={a.id} value={a.id}>{a.fullName}</option>)}
                </select>
              </div>
            )}
            {!isAgent && structures.length > 0 && (
              <div className="mb-3 flex flex-wrap items-center gap-2 rounded-r-lg border border-border bg-surface/30 p-2.5">
                <span className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Établissements à glisser :</span>
                {structures.map((s) => <StructChip key={s.id} structure={s} />)}
              </div>
            )}
            {/* Grille de planning UNIQUE — filtrée sur l'agent choisi (accent petite enfance) */}
            <PlanningGrilleUnifiee
              accent="petite_enfance"
              days={days}
              todayKey={todayKey}
              creneaux={creneauxGrille}
              filterAgentId={isAgent ? undefined : agentIndividuel}
              onCreneauClick={(c) => { const orig = creneaux.find((x) => x.id === c.id); if (orig && !isAgent) setEditCreneau(orig); }}
              colorOfCreneau={colorOfCreneauPe}
              nonTravailles={nonTravailles}
            />
            <DragOverlay>
              {dragStruct && (
                <div className="rounded-r-pill border border-petite_enfance/40 bg-petite_enfance-tint/90 px-3 py-1.5 text-xs font-semibold text-petite_enfance-fg shadow-pop">
                  {dragStruct.nom}
                </div>
              )}
            </DragOverlay>
          </DndContext>
        )}
      </main>

      {profilsOpen && (
        <ProfilsDialogPe
          agents={agents}
          structures={structures}
          onClose={() => setProfilsOpen(false)}
          onDone={() => { setProfilsOpen(false); void load(); }}
        />
      )}

      {/* Modale Postes unifiée (Affecter + créer/supprimer) — identique aux autres briques */}
      <PostesManagerUnifie
        open={postesOpen}
        onClose={() => setPostesOpen(false)}
        agents={agents}
        planningApi={postesApiPe}
        accent="petite_enfance"
        typeCreneau="accueil"
        titre="Postes — petite enfance"
        onChanged={() => { void load(); }}
      />
      {dialogElement}

      {affect && (
        <AffectStructureDialog
          structure={affect.structure}
          jour={affect.jour}
          agents={agents}
          onClose={() => setAffect(null)}
          onDone={() => { setAffect(null); void load(); }}
        />
      )}

      {/* Déclaration d'absence catégorisée — même modale que ST/GRC */}
      {absDecl && (
        <DeclarerAbsenceModal
          agent={absDecl.agent}
          jourISO={absDecl.jourISO}
          service="petite_enfance"
          onClose={() => setAbsDecl(null)}
          onDone={(conflits) => {
            setAbsDecl(null);
            void load();
            toast.success(conflits > 0
              ? `Absence enregistrée. ${conflits} tâche(s) à réaffecter — responsable alerté.`
              : 'Absence enregistrée.');
          }}
        />
      )}

      {/* Édition d'un créneau (horaires + structure) — avec Supprimer */}
      {editCreneau && (
        <EditCreneauPeDialog
          creneau={editCreneau}
          structures={structures}
          onClose={() => setEditCreneau(null)}
          onDone={() => { setEditCreneau(null); void load(); }}
          onDelete={async () => {
            const ok = await confirm({ title: 'Supprimer ce créneau ?', description: `${editCreneau.agentNom ?? ''} — ${editCreneau.structureNom ?? ''}`, confirmLabel: 'Supprimer', danger: true });
            if (!ok) return;
            await api.petiteEnfance.planning.remove(editCreneau.id);
            setEditCreneau(null);
            void load();
          }}
        />
      )}
    </PetiteEnfanceLayout>
  );
}

function StructChip({ structure }: { structure: PeStructure }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `struct-${structure.id}`, data: { structure } });
  return (
    <button ref={setNodeRef} {...listeners} {...attributes} type="button"
      className="inline-flex cursor-grab items-center gap-1 rounded-r-pill border border-petite_enfance/30 bg-petite_enfance-tint/60 px-2.5 py-1 text-xs font-semibold text-petite_enfance-fg active:cursor-grabbing"
      style={{ opacity: isDragging ? 0.4 : 1 }}>
      <GripVertical className="h-3 w-3 opacity-60" />
      {structure.nom}
    </button>
  );
}


// ── Affectation d'un établissement déposé (personne + heures), jour pré-rempli ──
function AffectStructureDialog({ structure, jour, agents, onClose, onDone }: {
  structure: PeStructure;
  jour: Date;
  agents: { id: string; fullName: string; role: string }[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [agentId, setAgentId] = useState('');
  // L'heure du drop (portée par `jour`) pré-remplit le début si elle est posée ;
  // sinon on retombe sur l'amplitude de la structure.
  const heureDrop = jour.getHours() > 0 ? `${String(jour.getHours()).padStart(2, '0')}:00` : null;
  const [debut, setDebut] = useState(heureDrop ?? structure.amplitudeDebut ?? '08:00');
  const [fin, setFin] = useState(structure.amplitudeFin ?? '18:00');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function affecter() {
    if (!agentId) { setError('Choisir une personne'); return; }
    setSaving(true); setError(null);
    const j = ymd(jour);
    const [hd, md] = debut.split(':').map(Number);
    const [hf, mf] = fin.split(':').map(Number);
    const dd = new Date(jour); dd.setHours(hd ?? 8, md ?? 0, 0, 0);
    const df = new Date(jour); df.setHours(hf ?? 18, mf ?? 0, 0, 0);
    void j;
    try {
      await api.petiteEnfance.planning.create({
        agentUserId: agentId,
        structureId: structure.id,
        dateDebut: dd.toISOString(),
        dateFin: df.toISOString(),
        titre: 'Accueil',
        type: 'accueil',
      });
      onDone();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open onClose={onClose} title={`Affecter « ${structure.nom} »`} size="sm">
      <div className="flex flex-col gap-3.5">
        <p className="text-xs text-ink-muted">{jour.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
        {error && <Alert variant="error">{error}</Alert>}
        <Field label="Personne" htmlFor="ap-agent" required>
          <select id="ap-agent" value={agentId} onChange={(e) => setAgentId(e.target.value)} className="h-11 w-full rounded-r-md border border-border bg-surface/60 px-3 text-sm text-ink">
            <option value="">—</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.fullName}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Début" htmlFor="ap-debut"><input id="ap-debut" type="time" value={debut} onChange={(e) => setDebut(e.target.value)} className="h-11 w-full rounded-r-md border border-border bg-surface/60 px-3 text-sm text-ink" /></Field>
          <Field label="Fin" htmlFor="ap-fin"><input id="ap-fin" type="time" value={fin} onChange={(e) => setFin(e.target.value)} className="h-11 w-full rounded-r-md border border-border bg-surface/60 px-3 text-sm text-ink" /></Field>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button variant="petite_enfance" loading={saving} onClick={() => void affecter()}>Affecter</Button>
        </div>
      </div>
    </Dialog>
  );
}

// ── Profils récurrents + génération sur période ────────────────────────────────
function ProfilsDialogPe({ agents, structures, onClose, onDone }: {
  agents: { id: string; fullName: string; role: string }[];
  structures: PeStructure[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [profils, setProfils] = useState<{ id: string; nom: string; type: string; plages: ProfilPlage[] }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  // nouveau profil
  const [nom, setNom] = useState('');
  const [jour, setJour] = useState(1);
  const [debut, setDebut] = useState('08:00');
  const [fin, setFin] = useState('18:00');
  const [plages, setPlages] = useState<ProfilPlage[]>([]);
  // génération
  const [profilId, setProfilId] = useState('');
  const [structureId, setStructureId] = useState(structures[0]?.id ?? '');
  const [agentIds, setAgentIds] = useState<string[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  useEffect(() => {
    api.petiteEnfance.planning.listProfils().then(setProfils).catch(() => setProfils([]));
  }, []);

  function ajouterPlage() {
    setPlages((p) => [...p, { jour, debut, fin }]);
  }

  async function creerProfil() {
    if (nom.trim().length < 2 || plages.length === 0) { setError('Nom + au moins une plage requis'); return; }
    setError(null);
    try {
      const p = await api.petiteEnfance.planning.createProfil({ nom: nom.trim(), type: 'accueil', plages });
      setProfils((prev) => [...prev, p]);
      setNom(''); setPlages([]); setMsg('Profil créé.');
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  async function generer() {
    if (!profilId || !structureId || agentIds.length === 0 || !from || !to) { setError('Profil, établissement, agents et période requis'); return; }
    setError(null); setMsg(null);
    try {
      const r = await api.petiteEnfance.planning.generer({ profilId, structureId, agentIds, from, to });
      setMsg(`${r.creneauxCrees} créneau(x) généré(s)${r.joursIgnores ? `, ${r.joursIgnores} jour(s) non travaillé(s) ignoré(s)` : ''}.`);
      onDone();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  return (
    <Dialog open onClose={onClose} title="Profils de planning &amp; génération" size="lg">
      <div className="flex flex-col gap-5">
        {error && <Alert variant="error">{error}</Alert>}
        {msg && <Alert variant="success">{msg}</Alert>}

        {/* Créer un profil */}
        <div className="rounded-r-lg border border-border bg-surface/40 p-4">
          <h3 className="mb-3 text-sm font-semibold text-ink">Nouveau profil</h3>
          <div className="mb-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <input value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Nom du profil" className="col-span-2 h-10 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink sm:col-span-1" />
            <select value={jour} onChange={(e) => setJour(Number(e.target.value))} className="h-10 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
              {JOURS.map((j, i) => <option key={i} value={i + 1}>{j}</option>)}
            </select>
            <input type="time" value={debut} onChange={(e) => setDebut(e.target.value)} className="h-10 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
            <input type="time" value={fin} onChange={(e) => setFin(e.target.value)} className="h-10 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
          </div>
          <div className="mb-2 flex flex-wrap gap-1.5">
            {plages.map((pl, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-r-pill bg-petite_enfance-tint/60 px-2 py-0.5 text-xs text-petite_enfance-fg">
                {JOURS[pl.jour - 1]} {pl.debut}–{pl.fin}
                <button type="button" onClick={() => setPlages((p) => p.filter((_, j) => j !== i))} className="text-ink-subtle">×</button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={ajouterPlage}><Plus className="h-4 w-4" /> Plage</Button>
            <Button variant="secondary" size="sm" onClick={() => void creerProfil()}>Enregistrer le profil</Button>
          </div>
        </div>

        {/* Générer */}
        <div>
          <h3 className="mb-2 text-sm font-semibold text-ink">Générer sur une période</h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Profil" htmlFor="g-profil" required>
              <select id="g-profil" value={profilId} onChange={(e) => setProfilId(e.target.value)} className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
                <option value="">— Choisir —</option>
                {profils.map((p) => <option key={p.id} value={p.id}>{p.nom} · {p.plages.length} plage(s)</option>)}
              </select>
            </Field>
            <Field label="Établissement" htmlFor="g-struct" required>
              <select id="g-struct" value={structureId} onChange={(e) => setStructureId(e.target.value)} className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
                {structures.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
              </select>
            </Field>
          </div>
          <div className="my-2">
            <span className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Agents</span>
            <div className="flex flex-wrap gap-1.5">
              {agents.map((a) => {
                const on = agentIds.includes(a.id);
                return (
                  <button key={a.id} type="button" onClick={() => setAgentIds((prev) => on ? prev.filter((x) => x !== a.id) : [...prev, a.id])}
                    className={['rounded-r-pill border px-2.5 py-1 text-xs', on ? 'border-petite_enfance bg-petite_enfance-tint/70 text-petite_enfance-fg' : 'border-border text-ink-muted'].join(' ')}>
                    {a.fullName}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Du" htmlFor="g-from" required><input id="g-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" /></Field>
            <Field label="Au" htmlFor="g-to" required><input id="g-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" /></Field>
          </div>
          <div className="mt-3 flex justify-end">
            <Button variant="petite_enfance" size="sm" onClick={() => void generer()}>Générer le planning</Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

// ── Édition d'un créneau (horaires + établissement) ────────────────────────────
function EditCreneauPeDialog({ creneau, structures, onClose, onDone, onDelete }: {
  creneau: PePlanningCreneau;
  structures: PeStructure[];
  onClose: () => void;
  onDone: () => void;
  onDelete?: () => Promise<void>;
}) {
  const jourISO = ymd(new Date(creneau.dateDebut));
  const [structureId, setStructureId] = useState(creneau.structureId);
  const [debut, setDebut] = useState(hhmmInput(creneau.dateDebut));
  const [fin, setFin] = useState(hhmmInput(creneau.dateFin));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function enregistrer() {
    setSaving(true); setError(null);
    const [hd, md] = debut.split(':').map(Number);
    const [hf, mf] = fin.split(':').map(Number);
    const dd = new Date(`${jourISO}T00:00:00`); dd.setHours(hd ?? 8, md ?? 0, 0, 0);
    const df = new Date(`${jourISO}T00:00:00`); df.setHours(hf ?? 18, mf ?? 0, 0, 0);
    try {
      await api.petiteEnfance.planning.update(creneau.id, {
        structureId,
        dateDebut: dd.toISOString(),
        dateFin: df.toISOString(),
      });
      onDone();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open onClose={onClose} title="Modifier le créneau" size="sm">
      <div className="flex flex-col gap-3.5">
        <p className="text-xs text-ink-muted">{new Date(`${jourISO}T00:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} · {creneau.agentNom}</p>
        {error && <Alert variant="error">{error}</Alert>}
        <Field label="Établissement" htmlFor="ed-struct">
          <select id="ed-struct" value={structureId} onChange={(e) => setStructureId(e.target.value)} className="h-11 w-full rounded-r-md border border-border bg-surface/60 px-3 text-sm text-ink">
            {structures.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Début" htmlFor="ed-debut"><input id="ed-debut" type="time" value={debut} onChange={(e) => setDebut(e.target.value)} className="h-11 w-full rounded-r-md border border-border bg-surface/60 px-3 text-sm text-ink" /></Field>
          <Field label="Fin" htmlFor="ed-fin"><input id="ed-fin" type="time" value={fin} onChange={(e) => setFin(e.target.value)} className="h-11 w-full rounded-r-md border border-border bg-surface/60 px-3 text-sm text-ink" /></Field>
        </div>
        <div className="flex items-center justify-between gap-2">
          {onDelete ? (
            <Button variant="danger" size="sm" onClick={() => void onDelete()}>Supprimer</Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Annuler</Button>
            <Button variant="petite_enfance" loading={saving} onClick={() => void enregistrer()}>Enregistrer</Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

function hhmmInput(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
